// ============================================================================
// Session handoff — a new tab borrows the sign-in of a tab already open
// ----------------------------------------------------------------------------
// The tokens and the chosen org live in sessionStorage (lib/auth.tsx says
// why), which is per tab. A tab the page opens with an opener gets a copy; one
// the browser opens itself — ctrl/cmd/middle-click, a bookmark, a pasted link
// — starts empty and lands on the sign-in screen while the person is plainly
// signed in next door.
//
// So a tab that starts without tokens asks. Every signed-in tab of this origin
// answers with its tokens and its org. A ctrl+click opens the new tab in the
// background, so the tab clicked in still has focus: its answer is taken at
// once, and the new tab opens in the same org. Otherwise — a bookmark, a
// pasted link — the asker waits briefly and takes the most recently used tab.
// No answer means no other tab is signed in, and the sign-in screen is the
// right thing to show.
//
// Nothing here outlives the last tab, which is the property sessionStorage was
// chosen for. A BroadcastChannel is same-origin, so eab.twodogs.ai and
// app.twodogs.ai never hand anything to each other, and nothing crosses to a
// script that could not already read sessionStorage.
//
// Signing out is broadcast too. Otherwise "sign out, open a new tab" would be
// signed straight back in by a tab left open.
// ============================================================================

import { ORG_PREFERENCE_KEY } from "@/lib/session";

const CHANNEL = "2dai.session-handoff";

/** How long a new tab waits for answers when no focused tab answers first. */
const WAIT_MS = 250;

type Message =
  | { type: "request"; id: string }
  | { type: "offer"; id: string; focused: boolean; activeAt: number; values: Record<string, string> }
  | { type: "signout" };

/** oidc-client-ts's key for the stored user, with WebStorageStateStore's prefix. */
export function oidcUserKey(authority: string, clientId: string): string {
  return `oidc.user:${authority}:${clientId}`;
}

function openChannel(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
}

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Still usable: a token that has expired is not worth handing on. */
function isLive(storedUser: string): boolean {
  try {
    const u = JSON.parse(storedUser) as { expires_at?: number };
    return typeof u.expires_at !== "number" || u.expires_at * 1000 > Date.now();
  } catch {
    return false;
  }
}

/** A sign-in callback in the URL belongs to this tab's own redirect. */
function hasAuthParams(): boolean {
  const q = new URLSearchParams(window.location.search);
  return Boolean((q.get("code") || q.get("error")) && q.get("state"));
}

let lastActive = typeof document !== "undefined" && document.hasFocus() ? Date.now() : 0;
let tracking = false;

function trackActivity(): void {
  if (tracking) return;
  tracking = true;
  const mark = () => {
    lastActive = Date.now();
  };
  // Capture, so a handler that stops propagation cannot hide the click that
  // opened the new tab.
  window.addEventListener("pointerdown", mark, true);
  window.addEventListener("keydown", mark, true);
  window.addEventListener("focus", mark);
}

/**
 * Answer other tabs' requests for as long as this tab is open. Returns the
 * cleanup.
 */
export function serveSession(userKey: string): () => void {
  const channel = openChannel();
  if (!channel) return () => undefined;
  trackActivity();
  channel.onmessage = (e: MessageEvent<Message>) => {
    if (e.data?.type !== "request") return;
    // Read at the moment of asking, so a token renewed in the background is
    // the one handed on.
    const user = read(userKey);
    if (!user || !isLive(user)) return;
    const values: Record<string, string> = { [userKey]: user };
    const org = read(ORG_PREFERENCE_KEY);
    if (org) values[ORG_PREFERENCE_KEY] = org;
    channel.postMessage({
      type: "offer",
      id: e.data.id,
      focused: document.hasFocus(),
      activeAt: lastActive,
      values,
    } satisfies Message);
  };
  return () => channel.close();
}

/**
 * Before the auth library reads storage: if this tab has no tokens, take them
 * from the focused tab, or failing that the most recently used one. Resolves
 * once storage is settled either way.
 */
export function receiveSession(userKey: string): Promise<void> {
  if (read(userKey) || hasAuthParams()) return Promise.resolve();
  const channel = openChannel();
  if (!channel) return Promise.resolve();

  const id = crypto.randomUUID();
  return new Promise((resolve) => {
    let best: Extract<Message, { type: "offer" }> | null = null;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      channel.close();
      if (best) {
        try {
          for (const [k, v] of Object.entries(best.values)) window.sessionStorage.setItem(k, v);
        } catch {
          /* no storage: the sign-in screen, as before */
        }
      }
      resolve();
    };

    channel.onmessage = (e: MessageEvent<Message>) => {
      const m = e.data;
      if (m?.type !== "offer" || m.id !== id || !m.values[userKey] || !isLive(m.values[userKey])) return;
      // Not "the first recent one": a tab clicked in a moment before the one
      // that opened us is recent too, and may well answer first.
      if (m.focused) {
        best = m;
        finish();
        return;
      }
      if (!best || m.activeAt > best.activeAt) best = m;
    };
    const timer = window.setTimeout(finish, WAIT_MS);
    channel.postMessage({ type: "request", id } satisfies Message);
  });
}

/** Tell every other tab of this origin to drop its tokens. */
export function broadcastSignOut(): void {
  const channel = openChannel();
  if (!channel) return;
  channel.postMessage({ type: "signout" } satisfies Message);
  channel.close();
}

/**
 * Run `onSignOut` when another tab signs out. Returns the cleanup.
 *
 * A channel hears every other channel of the same name, including ones in
 * this tab, so the tab that signed out hears itself too. `onSignOut` must
 * therefore only drop tokens, never navigate, or it would race that tab's own
 * redirect to the provider's sign-out.
 */
export function onSignOutElsewhere(onSignOut: () => void): () => void {
  const channel = openChannel();
  if (!channel) return () => undefined;
  channel.onmessage = (e: MessageEvent<Message>) => {
    if (e.data?.type === "signout") onSignOut();
  };
  return () => channel.close();
}
